import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../services/api"
import { FieldRow, FormPage } from "../../../components/FormPage"
import { ProductionKeyValuePairPost } from "../../../models/ProductionKeyValuePair"
import { ControllerRendererProps } from "../../../types/reactHookForm"
import { getErrorMessage } from "../../../utils/getErrorMessage"

const validationSchema = z.object({
  key: z.string().min(1, "Key is required"),
  value: z.string().min(1, "Value is required"),
})

export function KeyValuePairCreate() {
  const navigate = useNavigate()
  const returnURL = `/production#keyValuePairs`

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<ProductionKeyValuePairPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      key: "",
      value: "",
    })
  }, [reset])

  const onSubmit = useCallback(
    async (values: ProductionKeyValuePairPost) => {
      try {
        const response = await digitalManagerApi.post(`/production/keyValuePairs`, {
          key: values.key,
          value: values.value,
        })
        if (response.status === StatusCodes.CREATED) {
          toast.success("Production key-value created")
          navigate(returnURL)
        }
      } catch (error) {
        toast.error(getErrorMessage(error, "Cannot create production key-value"))
      }
    },
    [navigate, returnURL]
  )

  const submitForm = useCallback(() => {
    handleSubmit(onSubmit)()
  }, [handleSubmit, onSubmit])

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<ProductionKeyValuePairPost, "key" | "value">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderTextFieldKey = useCallback(
    ({ field }: ControllerRendererProps<ProductionKeyValuePairPost, "key">) => (
      <MUITextField
        id="key"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.key?.message === "string"
            ? { key: { message: errors.key.message } }
            : undefined
        }
      />
    ),
    [errors.key, handleTextFieldChange]
  )

  const renderTextFieldValue = useCallback(
    ({ field }: ControllerRendererProps<ProductionKeyValuePairPost, "value">) => (
      <MUITextField
        id="value"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.value?.message === "string"
            ? { value: { message: errors.value.message } }
            : undefined
        }
      />
    ),
    [errors.value, handleTextFieldChange]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={submitForm}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Key">
        <Controller name="key" control={control} render={renderTextFieldKey} />
      </FieldRow>
      <FieldRow label="Value">
        <Controller name="value" control={control} render={renderTextFieldValue} />
      </FieldRow>
    </FormPage>
  )
}
